import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import useBasket from '../hooks/basket/useBasket';
import { IMap, Order, OrderData } from '../services/exports/Interfaces';
import OrderManager from '../services/api/OrderManager';
import {
  COUPON_TYPES,
  DeliveryProvider,
  ERROR_CODES,
  ORDER_METHODS,
  PaymentMethod,
} from '../services/exports/Constants';
import { debounce } from 'lodash';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import collect from 'collect.js';
import moment from 'moment';
import DuplicateOrderModal from '../modals/checkout/DuplicateOrderModal';
import { PaymentIntent } from '@stripe/stripe-js';
import useAlert from '../hooks/utility/useAlert';
import useGoogleAnalytics from '../hooks/analytics/useGoogleAnalytics';
import {
  DataWrappedResponse,
  ErrorResponse,
  ReservedHttpResponseCodes,
  ValidationErrorResponse,
  ValidationErrorResponseData,
} from '../services/exports/Types';
import useAuth from '../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import useSessionStorage, { SESSION_STORAGE_KEYS } from '../hooks/global/useSessionStorage';

interface Props {
  children: ReactElement;
}

interface UpdateDataFunction {
  (field: string, value: any): void;
  (data: Partial<OrderData>): void;
}

interface OrderDraftContextInterface {
  data: Order;
  localData: Omit<OrderData, 'line_items'>;
  ongoingOrder: Order;
  loading: boolean;
  isOrderMethodInfoComplete: boolean;
  setOrderMethodInfoInfoComplete: () => void;
  isExternalDelivery: boolean;
  isCustomerInfoComplete: boolean;
  validationErrors: null | ValidationErrorResponseData;
  getOrderData: (data?: Partial<OrderData>) => OrderData;
  draftOrder: (data?: Partial<OrderData>) => Promise<DataWrappedResponse<Order, 200>>;
  createOrder: (
    paymentIntent: null | PaymentIntent,
    errorHandlers: IMap<() => void, string>,
    headers?: IMap<string>,
  ) => Promise<
    | DataWrappedResponse<Order, 201>
    | ErrorResponse<{ order: Order; payment_intent: PaymentIntent }, 402>
    | ErrorResponse<{ error_code: string }, 400>
    | ValidationErrorResponse
  >;
  checkOrderNotDuplicate: () => Promise<boolean>;
  refreshOngoingOrder: () => Promise<null | Order>;
  getOrderMethodName: (order: Partial<Order>) => string;
  addOngoingOrder: (data: Order) => void;
  removeOngoingOrder: (id: number) => void;
  setValidationErrors: (value: null | ValidationErrorResponseData) => void;
  resetValidationErrors: () => void;
  update: UpdateDataFunction;
}

export const OrderFlowContext = React.createContext<OrderDraftContextInterface>(null);

export enum ORDER_METHOD_INFO_COMPLETION_STATUS {
  complete = 'complete',
}

export default function OrderFlowProvider(props: Props): ReactElement {
  const { t } = useTranslation(undefined, { keyPrefix: 'Providers:OrderFlowProvider' });

  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order, ongoing_orders } = useSelector((state: StoreInterface) => state.order);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const alert = useAlert();
  const { isGuest } = useAuth();
  const { getItem, setItem } = useSessionStorage();
  const { trackBeginCheckout } = useGoogleAnalytics();
  const { selectedPaymentMethod, shouldSavePaymentInfo } = usePaymentFlow();
  const { data: basketData, items, formatItemForBackend } = useBasket();

  const isOrderMethodInfoComplete =
    getItem(SESSION_STORAGE_KEYS.order_method_info_status) === ORDER_METHOD_INFO_COMPLETION_STATUS.complete;
  const setOrderMethodInfoInfoComplete = () =>
    setItem(SESSION_STORAGE_KEYS.order_method_info_status, ORDER_METHOD_INFO_COMPLETION_STATUS.complete);

  const [draft, setDraft] = useState<Order>(cached_order as Order);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorResponseData>(null);
  const [loading, setLoading] = useState(false);
  const [duplicateOrderAware, setDuplicateOrderAware] = useState<boolean>(false);
  const [duplicateOrderModalProps, setDuplicateOrderModalProps] = useState({
    open: false,
    order: null,
  });

  const isExternalDelivery = useMemo(
    () =>
      cached_order.method === ORDER_METHODS.DELIVERY && cached_order.delivery_provider !== DeliveryProvider.Standalone,
    [cached_order.method, cached_order.delivery_provider],
  );

  const isCustomerInfoComplete = useMemo(
    () =>
      Boolean(cached_order.customer_phone_number) &&
      Boolean(cached_order.customer_email) &&
      Boolean(cached_order.customer_first_name) &&
      Boolean(cached_order.customer_last_name),
    [
      cached_order.customer_phone_number,
      cached_order.customer_email,
      cached_order.customer_first_name,
      cached_order.customer_last_name,
    ],
  );

  const ongoingOrder = useMemo(
    () => collect(ongoing_orders).firstWhere('company_id', company?.id),
    [ongoing_orders, company?.id],
  );

  const debouncedDraft = debounce(async (data?: OrderData) => draftOrder(data), 250);

  useEffect(() => {
    company?.id && debouncedDraft();

    return () => debouncedDraft.cancel();
  }, [
    company?.id,
    basketData,
    selectedPaymentMethod,
    cached_order.tip,
    cached_order.method,
    cached_order.coupon_type,
    cached_order.coupon_details,
    cached_order.delivery_lat,
    cached_order.delivery_lng,
    cached_order.delivery_quote?.id,
  ]);

  useEffect(() => {
    if (isReduxOrderValid()) {
      return;
    }

    return resetOrder();
  }, []);

  useEffect(() => {
    addMissingOrderFields();
  }, []);

  useEffect(() => {
    refreshOngoingOrder();
  }, []);

  const getOrderData = useCallback(
    (data?: Partial<OrderData>): OrderData => ({
      ...cached_order,
      method: cached_order.method,
      company_id: company?.id,
      payment_method_id: draft.total > 0 ? selectedPaymentMethod : PaymentMethod.Cash,
      should_save_payment_info: shouldSavePaymentInfo,
      delivery_quote_id: isExternalDelivery ? cached_order.delivery_quote?.id : null,
      promo: cached_order.coupon_details?.code,
      use_cashback: cached_order.coupon_type === COUPON_TYPES.CASHBACK,
      tip: cached_order.tip ?? 0,
      line_items: items.map((item) => formatItemForBackend(item)),
      ...data,
    }),
    [cached_order, company?.id, draft?.total, isExternalDelivery, selectedPaymentMethod, shouldSavePaymentInfo, items],
  );

  async function draftOrder(data?: OrderData) {
    setLoading(true);
    const request = await OrderManager.draftOrder({
      ...getOrderData(data),
      payment_intent_id: 'draft',
    });
    setTimeout(() => setLoading(false), 600);

    const { response, success } = request;

    if (success) {
      setDraft(response.data.data);
    }

    return request;
  }

  async function createOrder(
    paymentIntent: null | PaymentIntent,
    errorHandlers: IMap<() => void, string> = {},
    headers: IMap<string> = {},
  ) {
    const response = await OrderManager.postOrder(
      getOrderData({
        payment_intent_id: paymentIntent?.id,
      }),
      {
        [ERROR_CODES.CARD_EXCEPTION]: () => null,
        [ERROR_CODES.ORDER_FULFILLMENT_FAILED]: () => null,
        ...errorHandlers,
      },
      headers,
    );

    if (!response.success) {
      orderErrorHandler(response);

      return response;
    }

    trackBeginCheckout(response.response.data.data);

    return response;
  }

  function orderErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }
  }

  async function checkOrderNotDuplicate(): Promise<boolean> {
    if (duplicateOrderAware || isGuest) {
      return true;
    }

    const { success, response } = await OrderManager.getOrderHistory(company.id, { limit: 1 });

    if (!success) {
      return true;
    }

    const order = collect(response.data.data).first();

    if (order && moment().diff(moment(order.paid_at), 'minutes') < 15) {
      toggleDuplicateOrderModal(order);

      return false;
    }

    return true;
  }

  async function refreshOngoingOrder(): Promise<null | Order> {
    if (!ongoingOrder) {
      return;
    }

    const { success, response } = await OrderManager.getOrder(ongoingOrder.uuid);

    if (!success) {
      return;
    }

    if (moment(response.data.data.fulfillment.estimated_pickup_time).isBefore(moment().subtract(15, 'minutes'))) {
      ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(ongoingOrder.id));

      return null;
    }

    ReduxActions.dispatch(actionCreators.order.addOngoingOrder(response.data.data));

    return response.data.data;
  }

  function resetOrder() {
    if (!profile) {
      return;
    }

    ReduxActions.dispatch(
      actionCreators.order.resetOrder({
        customer_phone_number: profile.phone_number,
        customer_email: profile.email,
        customer_first_name: profile.first_name,
        customer_last_name: profile.last_name,
        marketing_consent: profile.is_marketing_subscriber,
      }),
    );
  }

  function addMissingOrderFields() {
    if (!profile) {
      return;
    }

    const replace = collect({
      customer_phone_number: profile.phone_number,
      customer_email: profile.email,
      customer_first_name: profile.first_name,
      customer_last_name: profile.last_name,
    }).filter((value, key) => !cached_order[key]).items;

    ReduxActions.dispatch(actionCreators.order.updateOrder(replace));
  }

  function isReduxOrderValid(): boolean {
    return !!cached_order?.method;
  }

  function toggleDuplicateOrderModal(order: Order = null): void {
    setDuplicateOrderModalProps({
      open: !!order,
      order,
    });
  }

  function getOrderMethodName(order: Partial<Order>): string {
    if (order.method === ORDER_METHODS.ROOM_SERVICE) {
      t(`order_methods.${order.method}.${order.room_service_location.type}`);
    }

    return t(`order_methods.${order.method}`);
  }

  const update: UpdateDataFunction = (field: string | Partial<OrderData>, value?: any) => {
    if (typeof field === 'object') {
      return ReduxActions.dispatch(actionCreators.order.updateOrder(field));
    }

    const parts = field.split('.');
    const key = parts[0];

    if (parts.length === 1) {
      return ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          [key]: value,
        }),
      );
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        [key]: {
          ...cached_order[key],
          [parts[1]]: value,
        },
      }),
    );
  };

  const context = useMemo(
    () => ({
      data: draft,
      localData: cached_order,
      ongoingOrder,
      loading,
      isOrderMethodInfoComplete,
      setOrderMethodInfoInfoComplete,
      isExternalDelivery,
      isCustomerInfoComplete,
      validationErrors,
      getOrderData,
      draftOrder,
      createOrder,
      checkOrderNotDuplicate,
      refreshOngoingOrder,
      getOrderMethodName,
      addOngoingOrder: (data: Order) => ReduxActions.dispatch(actionCreators.order.addOngoingOrder(data)),
      removeOngoingOrder: (id: number) => ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(id)),
      setValidationErrors,
      resetValidationErrors: () => setValidationErrors(null),
      update,
    }),
    [
      draft,
      loading,
      cached_order,
      ongoingOrder,
      isOrderMethodInfoComplete,
      isCustomerInfoComplete,
      validationErrors,
      draftOrder,
      getOrderData,
    ],
  );

  const renderDuplicateOrderModal = useMemo(() => {
    return (
      <DuplicateOrderModal
        open={duplicateOrderModalProps.open}
        order={duplicateOrderModalProps.order}
        onConfirm={() => setDuplicateOrderAware(true)}
        toggleModal={toggleDuplicateOrderModal}
      />
    );
  }, [duplicateOrderModalProps]);

  return (
    <OrderFlowContext.Provider value={context}>
      {children}
      {renderDuplicateOrderModal}
    </OrderFlowContext.Provider>
  );
}
